import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { DR_PHOTO } from "@/lib/assets";

const qualifications = [
  { abbr: "PhD", detail: "Restorative Dentistry, University of Manchester" },
  { abbr: "MFDS", detail: "RCS England & Edinburgh" },
  { abbr: "FFD", detail: "RCSI Ireland" },
  { abbr: "FDS", detail: "RCPS Glasgow" },
  { abbr: "FHEA", detail: "Higher Education Academy" },
];

export default function AboutSection({ onBook }) {
  return (
    <section id="about-preview" data-testid="about-section" className="py-24 md:py-28 lg:py-32" style={{ background: '#FAFAF9' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8">

        {/* ── Mobile: stacked ── Tablet: side-by-side ── Desktop: 12-col grid ── */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 lg:gap-16 items-start">

          {/* Photo column */}
          <div className="reveal md:col-span-4 lg:col-span-4">
            <div className="relative">
              {/* Decorative offset border */}
              <div
                className="absolute pointer-events-none"
                style={{ top: '-12px', left: '-12px', right: '12px', bottom: '12px', border: '1px solid #E7E5E4', zIndex: 0 }}
              />
              {/* Photo wrapper — portrait on desktop, landscape-ish on tablet */}
              <div
                className="relative overflow-hidden"
                style={{ zIndex: 1, aspectRatio: '3/4', maxHeight: '480px' }}
              >
                <img
                  src={DR_PHOTO}
                  alt="Dr. Shihab Romeed"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: '50% 12%' }}
                />
              </div>
              {/* Dual-brand accent bar */}
              <div
                className="absolute bottom-0 left-0 right-0"
                style={{ height: '3px', background: 'linear-gradient(90deg, #D4AF37 50%, #1CB0C6 50%)', zIndex: 2 }}
              />
            </div>
          </div>

          {/* Bio column */}
          <div className="reveal md:col-span-8 lg:col-span-8" style={{ animationDelay: '0.12s' }}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-10 h-px" style={{ background: '#D4AF37' }} />
              <span className="font-outfit text-xs tracking-[0.25em] uppercase" style={{ color: '#78716C' }}>
                Clinical Director
              </span>
            </div>

            <h2
              className="font-cormorant font-light mb-3 leading-tight"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)', color: '#1C1917' }}
            >
              Dr. Shihab Romeed
            </h2>

            <p className="font-outfit text-sm font-medium tracking-wider uppercase mb-5" style={{ color: '#D4AF37' }}>
              Consultant in Restorative Dentistry &middot; Implant Dentist &middot; Educator
            </p>

            <p className="font-outfit text-base leading-relaxed mb-3" style={{ color: '#1C1917' }}>
              Dr. Romeed is Clinical Director and Principal Implant Dentist at Specialist Dental London, with more than 25 years of experience in advanced restorative and implant dentistry.
            </p>

            <p className="font-outfit text-sm leading-relaxed mb-6" style={{ color: '#78716C' }}>
              UK-trained with specialist registration across Restorative Dentistry, Prosthodontics, Periodontics, and Endodontics. He completed a PhD at the University of Manchester and higher specialist training at King's College London.
            </p>

            {/* Stats row — compact on tablet */}
            <div className="flex flex-wrap gap-6 mb-6 pb-6" style={{ borderBottom: '1px solid #E7E5E4' }}>
              {[
                { num: '25+', label: 'Years Experience' },
                { num: '4', label: 'Royal College Fellowships' },
                { num: "King's", label: 'College London' },
              ].map(s => (
                <div key={s.label}>
                  <span className="font-cormorant block leading-none" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', color: '#1C1917' }}>
                    {s.num}
                  </span>
                  <span className="font-outfit text-xs tracking-wider uppercase" style={{ color: '#78716C' }}>
                    {s.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Qualifications — tooltip badges */}
            <div className="flex flex-wrap gap-2 mb-7">
              {qualifications.map(q => (
                <div key={q.abbr} className="group relative">
                  <div
                    className="font-outfit text-xs font-semibold px-3 py-1.5 cursor-default border transition-colors duration-200 hover:border-stone-400"
                    style={{ border: '1px solid #E7E5E4', color: '#1C1917', background: '#FFFFFF' }}
                  >
                    {q.abbr}
                  </div>
                  <div
                    className="absolute bottom-full left-0 mb-2 px-3 py-2 font-outfit text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10"
                    style={{ background: '#1C1917', color: '#FAFAF9' }}
                  >
                    {q.detail}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/about"
                data-testid="about-full-bio-link"
                className="flex items-center gap-2 font-outfit text-sm font-medium px-6 py-3 transition-all duration-200 hover:opacity-90 active:scale-95"
                style={{ background: '#1C1917', color: '#FAFAF9' }}
              >
                Full Biography <ArrowRight size={15} />
              </Link>
              <button
                data-testid="about-book-btn"
                onClick={() => onBook(null)}
                className="flex items-center gap-2 font-outfit text-sm font-medium px-6 py-3 border transition-all duration-200 hover:border-stone-800 active:scale-95"
                style={{ border: '1px solid #E7E5E4', color: '#1C1917', background: 'transparent' }}
              >
                Book an Appointment
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
